// m2-station-store.js — station persistence via localStorage (ARD M2 contract).
// Exposes window.M2 = { getStations, setStation, removeStation, reorderStations,
//                       getLastPlayedIndex, setLastPlayedIndex, storeLocalAudio,
//                       exportRoster, importRoster, setLocked, getLocked,
//                       setQualityPreference, getQualityPreference, isPersistent }.

window.M2 = (() => {
  // localStorage keys (namespaced per ARD)
  const KEY_STATIONS    = 'radio.stations';
  const KEY_LAST_PLAYED = 'radio.lastPlayed';
  const KEY_QUALITY     = 'radio.quality';

  // Internal persistence schema version (distinct from portable export version).
  const PERSISTENCE_VERSION = 2;

  // In-memory fallback used only when localStorage is unavailable
  let _memStations   = new Array(10).fill(null);
  let _memLastPlayed = null;
  let _memQuality    = 'normal';
  let _storageOk     = null; // null = not yet checked

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  function _storageAvailable() {
    if (_storageOk !== null) return _storageOk;
    try {
      localStorage.setItem('radio._probe', '1');
      localStorage.removeItem('radio._probe');
      _storageOk = true;
    } catch (e) {
      _storageOk = false;
    }
    return _storageOk;
  }

  function _readStations() {
    if (!_storageAvailable()) return [..._memStations];
    try {
      const raw = localStorage.getItem(KEY_STATIONS);
      if (!raw) return new Array(10).fill(null);
      const parsed = JSON.parse(raw);

      // Versioned format: { version: N, stations: Array<10> }
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && parsed.version && Array.isArray(parsed.stations)) {
        if (parsed.stations.length === 10) {
          return parsed.stations.map(slot => slot ? { ...slot, locked: slot.locked === true } : null);
        }
        return new Array(10).fill(null);
      }

      // Legacy bare-array format (no version wrapper) — migrate on first read
      if (Array.isArray(parsed) && parsed.length === 10) {
        const migrated = parsed.map(slot => slot ? { ...slot, locked: slot.locked === true } : null);
        // Write back immediately in versioned format
        _writeRawStations(migrated);
        return migrated;
      }

      // Corrupted or wrong shape — return clean state (M2-012)
      return new Array(10).fill(null);
    } catch (e) {
      Logger.log({ ts: Date.now(), module: 'M2', fn: '_readStations', inputs: {}, output: null, durationMs: 0, error: e });
      return new Array(10).fill(null);
    }
  }

  /** Writes the station array in the versioned persistence format. */
  function _writeRawStations(arr) {
    try {
      localStorage.setItem(KEY_STATIONS, JSON.stringify({
        version: PERSISTENCE_VERSION,
        stations: arr
      }));
    } catch (e) {
      Logger.log({ ts: Date.now(), module: 'M2', fn: '_writeRawStations', inputs: {}, output: null, durationMs: 0, error: e });
    }
  }

  function _writeStations(arr) {
    if (!_storageAvailable()) {
      _memStations = arr;
      return;
    }
    _writeRawStations(arr);
  }

  function _validIndex(index) {
    return Number.isInteger(index) && index >= 1 && index <= 10;
  }

  // ---------------------------------------------------------------------------
  // Public interface (ARD M2 contract)
  // ---------------------------------------------------------------------------

  /**
   * Returns an ordered array of occupied station objects { index, url, label, locked, url_lq, url_hq }.
   * Empty slots are omitted. Returns [] when no stations are saved.
   */
  const getStations = Logger.traced('M2', 'getStations', () => {
    const arr = _readStations();
    return arr
      .map((slot, i) => (slot ? { index: i + 1, url: slot.url, label: slot.label, locked: slot.locked === true, url_lq: slot.url_lq || '', url_hq: slot.url_hq || '' } : null))
      .filter(Boolean);
  });

  /**
   * Assigns or overwrites a station slot (1–10).
   * Accepts optional url_lq and url_hq for multi-quality support.
   * New stations are initialized with locked: false.
   * Returns { index, url, label, url_lq, url_hq } on success or { error } on invalid input.
   */
  const setStation = Logger.traced('M2', 'setStation', (index, url, label, url_lq, url_hq) => {
    if (!_validIndex(index)) {
      return { error: `Invalid index ${index}: must be 1–10` };
    }
    const arr = _readStations();
    arr[index - 1] = { url, label, locked: false, url_lq: url_lq || '', url_hq: url_hq || '' };
    _writeStations(arr);
    return { index, url, label, url_lq: url_lq || '', url_hq: url_hq || '' };
  });

  /**
   * Clears a station slot, also removing any cached local-audio file.
   * Returns true on success or { error } on invalid input.
   */
  function _removeStationImpl(index) {
    if (!_validIndex(index)) {
      return { error: `Invalid index ${index}: must be 1–10` };
    }
    const arr = _readStations();
    const slot = arr[index - 1];
    if (slot && slot.url) _removeLocalAudio(slot.url);
    arr[index - 1] = null;
    _writeStations(arr);
    return true;
  }

  const removeStation = Logger.traced('M2', 'removeStation', _removeStationImpl);

  /**
   * Swaps the content of two station slots.
   * Returns true on success (including equal-index no-op) or { error } on invalid input.
   */
  const reorderStations = Logger.traced('M2', 'reorderStations', (fromIndex, toIndex) => {
    if (!_validIndex(fromIndex) || !_validIndex(toIndex)) {
      return { error: `Invalid index: fromIndex=${fromIndex}, toIndex=${toIndex}` };
    }
    if (fromIndex === toIndex) {
      return true; // no-op (M2-008)
    }
    const arr          = _readStations();
    const tmp          = arr[fromIndex - 1];
    arr[fromIndex - 1] = arr[toIndex   - 1];
    arr[toIndex   - 1] = tmp;
    _writeStations(arr);
    return true;
  });

  /**
   * Returns the last played station index (1–10) or null if never set / unavailable.
   */
  const getLastPlayedIndex = Logger.traced('M2', 'getLastPlayedIndex', () => {
    if (!_storageAvailable()) {
      return _memLastPlayed;
    }
    const raw = localStorage.getItem(KEY_LAST_PLAYED);
    if (raw === null) {
      return null;
    }
    const parsed = parseInt(raw, 10);
    return (_validIndex(parsed)) ? parsed : null;
  });

  /**
   * Persists the last played station index (1–10).
   * Returns true on success or { error } on invalid input.
   */
  const setLastPlayedIndex = Logger.traced('M2', 'setLastPlayedIndex', (index) => {
    if (!_validIndex(index)) {
      return { error: `Invalid index ${index}: must be 1–10` };
    }
    if (_storageAvailable()) {
      localStorage.setItem(KEY_LAST_PLAYED, String(index));
    } else {
      _memLastPlayed = index;
    }
    return true;
  });

  // ---------------------------------------------------------------------------
  // Local audio file storage via Cache API
  // ---------------------------------------------------------------------------

  const LOCAL_AUDIO_CACHE = 'radio-local-audio';

  /**
   * Stores an uploaded audio file in the browser Cache API and returns
   * the cache key URL (e.g. '/local-audio/song-12345-1679000000').
   * Returns null if Cache API is unavailable.
   */
  const storeLocalAudio = Logger.tracedAsync('M2', 'storeLocalAudio', async (file) => {
    if (!('caches' in window)) return null;
    const key = `/local-audio/${file.name}-${file.size}-${file.lastModified}`;
    const cache = await caches.open(LOCAL_AUDIO_CACHE);
    const response = new Response(file, {
      headers: { 'Content-Type': file.type || 'audio/mpeg' }
    });
    await cache.put(new Request(key), response);
    return key;
  });

  /**
   * Removes a local audio file from Cache API if the URL is a local-audio key.
   */
  async function _removeLocalAudio(url) {
    if (!url || !url.startsWith('/local-audio/')) return;
    if (!('caches' in window)) return;
    try {
      const cache = await caches.open(LOCAL_AUDIO_CACHE);
      await cache.delete(new Request(url));
    } catch (e) { /* best-effort cleanup */ }
  }

  // removeStation (above) is a single traced wrapper that clears the slot AND
  // cleans up any cached local-audio file. (Previously this was double-wrapped
  // by Logger.traced, causing every removal to log twice — fixed in GLM Batch A.)

  // ---------------------------------------------------------------------------
  // Roster export / import (ARD M2 contract)
  // ---------------------------------------------------------------------------

  /**
   * Serializes the current 10-slot roster to a portable JSON object.
   * Portable format uses version:1 (distinct from internal persistence version:2).
   * Returns { version: 1, exported: ISO-8601, stations: [{slot, url, label}, ...] }.
   */
  const exportRoster = Logger.traced('M2', 'exportRoster', () => {
    const arr = _readStations();
    return {
      version: 1,
      exported: new Date().toISOString(),
      stations: arr.map((slot, i) => slot
        ? { slot: i + 1, url: slot.url, label: slot.label, locked: slot.locked === true, url_lq: slot.url_lq || '', url_hq: slot.url_hq || '' }
        : { slot: i + 1, url: null, label: null, locked: false, url_lq: '', url_hq: '' }
      )
    };
  });

  /**
   * Validates and imports a roster object, replacing all 10 slots.
   * Returns { success: boolean, error?: string }.
   */
  const importRoster = Logger.traced('M2', 'importRoster', (data) => {
    if (!data || typeof data !== 'object') {
      return { success: false, error: 'Invalid import data' };
    }
    if (data.version !== 1) {
      return { success: false, error: 'Unsupported roster version' };
    }
    if (!Array.isArray(data.stations) || data.stations.length !== 10) {
      return { success: false, error: 'Roster must contain exactly 10 station entries' };
    }
    const arr = data.stations.map(entry =>
      entry && entry.url ? { url: entry.url, label: entry.label || '', locked: entry.locked === true, url_lq: entry.url_lq || '', url_hq: entry.url_hq || '' } : null
    );
    _writeStations(arr);
    return { success: true };
  });

  // ---------------------------------------------------------------------------
  // Lock state (ARD M2 contract — setLocked / getLocked)
  // ---------------------------------------------------------------------------

  /**
   * Sets the locked state for the station at the given slot (1–10).
   * Persists to localStorage.
   * Returns { success: true } or { success: false, error: string }.
   */
  const setLocked = Logger.traced('M2', 'setLocked', (index, locked) => {
    if (!_validIndex(index)) {
      return { success: false, error: `Invalid index ${index}: must be 1–10` };
    }
    const arr = _readStations();
    const slot = arr[index - 1];
    if (!slot) {
      return { success: false, error: `Slot ${index} is empty` };
    }
    arr[index - 1] = { ...slot, locked: locked === true };
    _writeStations(arr);
    return { success: true };
  });

  /**
   * Returns the locked state for the station at the given slot (1–10).
   * Returns false for empty slots or out-of-range indices.
   */
  const getLocked = Logger.traced('M2', 'getLocked', (index) => {
    if (!_validIndex(index)) return false;
    const arr = _readStations();
    const slot = arr[index - 1];
    if (!slot) return false;
    return slot.locked === true;
  });

  // ---------------------------------------------------------------------------
  // Quality preference (ARD M2 contract — setQualityPreference / getQualityPreference)
  // ---------------------------------------------------------------------------

  const VALID_QUALITIES = ['data-saver', 'normal', 'high'];

  /**
   * Persists the user's quality preference.
   * Accepts "data-saver", "normal", or "high".
   * Returns { success: true } or { success: false, error: string }.
   */
  const setQualityPreference = Logger.traced('M2', 'setQualityPreference', (quality) => {
    if (!VALID_QUALITIES.includes(quality)) {
      return { success: false, error: `Invalid quality "${quality}": must be one of data-saver, normal, high` };
    }
    if (_storageAvailable()) {
      localStorage.setItem(KEY_QUALITY, quality);
    } else {
      _memQuality = quality;
    }
    return { success: true };
  });

  /**
   * Returns the user's quality preference.
   * Defaults to "normal" when nothing is saved or storage is unavailable.
   */
  const getQualityPreference = Logger.traced('M2', 'getQualityPreference', () => {
    if (!_storageAvailable()) return _memQuality;
    const raw = localStorage.getItem(KEY_QUALITY);
    if (raw && VALID_QUALITIES.includes(raw)) return raw;
    return 'normal';
  });

  /**
   * Returns true when localStorage is available and writable.
   * When false, writes are held in an in-memory fallback that does not
   * survive page reloads — the user should be notified.
   */
  const isPersistent = Logger.traced('M2', 'isPersistent', () => {
    return _storageAvailable();
  });

  return { getStations, setStation, removeStation, reorderStations, getLastPlayedIndex, setLastPlayedIndex, storeLocalAudio, exportRoster, importRoster, setLocked, getLocked, setQualityPreference, getQualityPreference, isPersistent };
})();
